import { useAuth } from '@/composables/useAuth'
import { useToast } from '@/features/shared/composables/useToast'
import router from '@/router'
import { normalizeApiErrorResponse } from '@/utils/normalizeApiErrorResponse'
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

// Per-hostname API targets: each deployed business-admin host talks to its own backend
// regardless of the baked VITE_API_BASE_URL. business-staging -> business-hotfix (our
// functional staging backend); business (prod) -> business-api. The map is exhaustive
// for stable deployed hosts, so VITE_API_BASE_URL is only a fallback for preview /
// branch-deploy hosts (e.g. the dheeraj_develop dev preview -> business-develop). The
// business-staging-api and business-staging2-api backends are stale/mothballed and are
// NOT used.
const HOSTNAME_API_TARGETS: Record<string, string> = {
  'business-staging.pour.now': 'https://business-api-staging.pour.now/api/v1',
  'business.pour.now': 'https://business-api.pour.now/api/v1',
}

// Safe non-prod default for unknown / preview hosts when neither the host map nor a
// build-time VITE_API_BASE_URL is present (e.g. a fresh checkout without a local .env,
// now that .env is untracked). Never defaults to prod.
const DEFAULT_API_BASE_URL = 'https://business-api-develop.pour.now/api/v1'

export const api = axios.create({
  baseURL: HOSTNAME_API_TARGETS[window.location.hostname] ?? import.meta.env.VITE_API_BASE_URL ?? DEFAULT_API_BASE_URL,
})

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig & { skipToastError?: boolean }) => {
    const businessStore = useBusinessStore()

    config.headers['X-Business-ID'] = businessStore.selectedBusiness?.id
    config.headers['language'] = 'en-us'
    config.headers['accept-Language'] = 'en-us'
    config.paramsSerializer = {
      indexes: null,
    }

    const token = useAuthStore().token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

type ApiErrorCode =
  | 'BAD_REQUEST'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'METHOD_NOT_ALLOWED'
  | 'NOT_ACCEPTABLE'
  | 'REQUEST_TIMEOUT'
  | 'CONFLICT'
  | 'GONE'
  | 'PAYLOAD_TOO_LARGE'
  | 'URI_TOO_LONG'
  | 'UNSUPPORTED_MEDIA_TYPE'
  | 'UNPROCESSABLE_ENTITY'
  | 'TOO_MANY_REQUESTS'
  | 'SERVER_ERROR'

export interface ApiError {
  error_code: ApiErrorCode
  error_message: string
  status_code: number
  details: string | Record<string, string[]>
}

api.interceptors.response.use(
  (response) => {
    return response
  },
  (error: AxiosError<ApiError>) => {
    const { showToast } = useToast()
    const { logout } = useAuth()

    const config = error.config as InternalAxiosRequestConfig & { skipToastError?: boolean }

    try {
      if (error.response?.status === 401) {
        if (!config.skipToastError) {
          showToast({ text: error.response.data.error_message }, 'error')
        }
        if (router.currentRoute.value.meta.requiresAuth) {
          logout()
        }
        return
      }

      if (error.response && !config.skipToastError) {
        const normalizedError = normalizeApiErrorResponse(error.response.data)

        showToast(
          {
            text: normalizedError.message || 'Something went wrong',
            list: normalizedError.details,
          },
          'error',
        )
      }
    } catch (error) {
      console.log(error)
    }
    return Promise.reject(error)
  },
)

export default api
